import { useAffectedEntities } from '@/hooks/useAffectedEntities';
import { getGMTOffset } from '@/utils/calendar';
import { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

// Define interface for sending messages to Steno
interface StenoMessage {
  action: string;
  data?: any;
}

function StenoAiChatbot() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { processAffectedEntities } = useAffectedEntities();

  // Function to find the Steno iframe in the DOM
  const findStenoIframe = useCallback(() => {
    if (!iframeRef.current) {
      const iframes = document.querySelectorAll('iframe');
      for (const iframe of iframes) {
        if (
          iframe.id === 'chat-iframe' ||
          iframe.src.includes('rpmlanner.com') ||
          iframe.src.includes('chat') ||
          iframe.id.includes('chat')
        ) {
          iframeRef.current = iframe;
          break;
        }
      }
    }
    return iframeRef.current;
  }, []);

  // Function to send messages to the Steno iframe
  const sendMessageToSteno = useCallback(
    (message: StenoMessage) => {
      const iframe = findStenoIframe();
      if (iframe) {
        const targetOrigin = new URL(iframe.src).origin;
        iframe.contentWindow?.postMessage(message, targetOrigin);
        return true;
      }
      return false;
    },
    [findStenoIframe],
  );

  // Handle chat opened message
  const handleChatOpened = useCallback(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userOffset = getGMTOffset(userTimezone);

    sendMessageToSteno({
      action: 'user-timezone-info',
      data: {
        timezone: userTimezone,
        offset: userOffset,
      },
    });
  }, [sendMessageToSteno]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      switch (event.data.action) {
        case 'chat-opened':
          handleChatOpened();
          break;

        case 'message-submitted':
          break;

        case 'AFFECTED_ENTITIES_UPDATE': {
          const entities = event.data.payload?.affected_entities;
          if (entities?.length > 0) {
            processAffectedEntities(entities);
          }
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleChatOpened, processAffectedEntities]);

  //Changes here to trigger a deployment
  return (
    <Helmet>
      <script
        src="https://cdn.jsdelivr.net/gh/aisteno/embed@latest/steno-chat.js"
        data-id="tony-rpm"
        data-url={import.meta.env.VITE_STENO_DATA_URL}
        data-origin="/rpm/tony-rpm"
        data-position="right"
      />
    </Helmet>
  );
}

export default StenoAiChatbot;
